import type { ActionsNetwork } from 'shared/store/actions'
import { fetchPageData, updateToken } from 'shared/store/actions'
import endpoints from 'shared/store/endpoints'
import { ws } from 'shared/store/ws'
import type { SignUpForm, SignupPage } from 'shared/types/api'
import type { Dispatch } from 'redux'
import type { RootState } from 'shared/store/reducers'
import { errors } from 'shared/utils'
import { getWebsocket, sendToken } from 'shared/store/websocket'
import { getAnalyticsData } from 'shared/store/user'
import { updateDeliveryRestaurant } from 'shared/store/ecommerce/order'

type GetState = () => RootState

export type ActionSignupPageDataResponse = {
    type: 'PAGE_DATA_SIGNUP_RESPONSE',
    data: SignupPage
}

type ActionSignupLogout = {
    type: 'LOGOUT'
}

type ActionSignupSendFormError = {
    type: 'SIGNUP_SEND_FORM_ERROR',
    sendFormError: any
}

type ActionSignupResendMailError = {
    type: 'SIGNUP_RESEND_MAIL_ERROR',
    resendMailError: any
}

type ActionSignupResendMailSuccess = {
    type: 'SIGNUP_RESEND_MAIL_SUCCESS'
}

type ActionSignupSendFormSuccess = {
    type: 'SIGNUP_SEND_FORM_SUCCESS',
    login: string,
    loginType: string
}

type ActionSignupSendFormConnected = {
    type: 'SIGNUP_SEND_FORM_CONNECTED',
    token: string
}

type ActionSignupReset = {
    type: 'SIGNUP_RESET'
}

type ActionSignupError = {
    type: 'SIGNUP_ERROR',
    error: any
}

const fetchSignupPageData = () =>
    fetchPageData(endpoints.kingdom.signup, 'PAGE_DATA_SIGNUP_RESPONSE')

const resendMail = (queen: string) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const { login } = getState().signupPageState
        try {
            await ws.get(endpoints.kingdom.activate, {
                params: { email: login, queen: queen }
            })
            dispatch(actionResendMailSuccess())
        } catch (error) {
            console.error('Error resending activation email :', error)
            dispatch(actionResendMailError(error))
        }
    }
}

const actionResendMailSuccess = (): ActionSignupResendMailSuccess => ({
    type: 'SIGNUP_RESEND_MAIL_SUCCESS'
})

const actionResendMailError = (
    resendMailError: any
): ActionSignupResendMailError => ({
    type: 'SIGNUP_RESEND_MAIL_ERROR',
    resendMailError
})

const sendSignupForm = (form: SignUpForm) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await ws.post(endpoints.kingdom.signup, form)
            if (
                response &&
                response.headers &&
                response.headers.authorization
            ) {
                const token = response.headers.authorization
                await dispatch(updateToken(token))
                dispatch(actionSendFormConnected(token))
                dispatch(getAnalyticsData())
                await dispatch(updateDeliveryRestaurant())
                /** When we signup via social connect, add token to WebSocket **/
                // FIXME: No platform specific code in shared
                if (ws.defaults.headers['x-platform'] === 'WEB') {
                    sendToken(token)
                    getWebsocket()
                }
            } else {
                dispatch(actionSendFormSuccess(form.login, form.loginType))
            }
        } catch (error) {
            const { response } = error
            console.error('Error in sign up', response)
            let message = 'form.error.default'
            if (response) {
                const { data } = response
                if (data) {
                    switch (data.message) {
                        case errors.LOYALTY_NUMBER_ALREADY_USED:
                            message = 'signup.form.send.error.loyalty'
                            break
                        case errors.TOO_YOUNG:
                            message = 'signup.form.send.error.young'
                            break
                        case errors.LOGIN_ALREADY_USED:
                            message = 'form.error.login.already.used'
                            break
                        case errors.LOGIN_TYPE_INVALID:
                            /* TODO: When we implement Facebook/Google connexion
                            message = 'signin.form.error.login.type.invalid'
                            */
                            break
                        case errors.UID_ALREADY_USED:
                            message = 'signup.form.send.error.social'
                            break
                        case errors.INVALID_EMAIL:
                            message = 'signup.form.send.error.email'
                            break
                        case errors.PASSWORD_WRONG_FORMAT:
                            message = 'signup.form.send.error.password'
                            break
                        case errors.CANNOT_PARSE_BIRTHDATE:
                        default:
                            break
                    }
                }
            }
            dispatch(actionSendFormError(message))
        }
    }
}

const actionSendFormSuccess = (
    login: string,
    loginType: string
): ActionSignupSendFormSuccess => ({
    type: 'SIGNUP_SEND_FORM_SUCCESS',
    login,
    loginType
})

const actionSendFormConnected = (
    token: string
): ActionSignupSendFormConnected => ({
    type: 'SIGNUP_SEND_FORM_CONNECTED',
    token
})

const actionSendFormError = (
    sendFormError: any
): ActionSignupSendFormError => ({
    type: 'SIGNUP_SEND_FORM_ERROR',
    sendFormError
})

const resetSignup = (error: any): ActionSignupReset => ({
    type: 'SIGNUP_RESET',
    error
})

const resetError = (error: any): ActionSignupError => ({
    type: 'SIGNUP_ERROR',
    error
})

export {
    fetchSignupPageData,
    sendSignupForm,
    resendMail,
    resetSignup,
    resetError
}

export type Action =
    | ActionSignupPageDataResponse
    | ActionSignupLogout
    | ActionSignupSendFormError
    | ActionSignupSendFormSuccess
    | ActionSignupResendMailSuccess
    | ActionSignupResendMailError
    | ActionSignupError
    | ActionSignupReset
    | ActionSignupSendFormConnected
    | ActionsNetwork
